'use client';

import { useEffect, useRef } from 'react';
import type { HelpLocale, HelpVideo } from '@/content/reseller-help/types';

const TRACK_LABELS: Record<HelpLocale, string> = { en: 'English', nl: 'Nederlands', de: 'Deutsch', fr: 'Français' };

export function formatVideoTime(seconds: number) {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Engelse uitlegvideo met ondertitels. Standaardondertitel: de taal van de
 * pagina als die er is, anders Engels. Hoofdstukken springen naar hun tijd.
 */
export function HelpVideoPlayer({ video, locale, title, labels }: {
  video: HelpVideo;
  locale: HelpLocale;
  title: string;
  labels: { video: string; chapters: string; fallback: string; jumpTo: string };
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const tracks = (Object.entries(video.captions) as Array<[HelpLocale, string | undefined]>).filter((entry): entry is [HelpLocale, string] => Boolean(entry[1]));
  const defaultTrack = video.captions[locale] ? locale : 'en';

  // ?t=<seconden> (deeplink naar een hoofdstuk, zie VideoObject Clip-url's).
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    let start = 0;
    try {
      start = Number(new URLSearchParams(window.location.search).get('t')) || 0;
    } catch { /* geen startpunt */ }
    if (start <= 0) return;
    const apply = () => { element.currentTime = start; };
    element.preload = 'metadata';
    element.addEventListener('loadedmetadata', apply, { once: true });
    return () => element.removeEventListener('loadedmetadata', apply);
  }, []);

  function seek(t: number) {
    const element = ref.current;
    if (!element) return;
    element.currentTime = t;
    void element.play().catch(() => undefined);
    element.focus();
  }

  return (
    <section aria-label={`${labels.video}: ${title}`} className="mt-8">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-950 shadow-sm">
        <video
          ref={ref}
          controls
          preload="none"
          playsInline
          poster={video.poster}
          className="aspect-video w-full bg-slate-950"
          aria-label={title}
          crossOrigin="anonymous"
        >
          <source src={video.src} type="video/mp4" />
          {tracks.map(([lang, src]) => (
            <track key={lang} kind="subtitles" src={src} srcLang={lang} label={TRACK_LABELS[lang]} default={lang === defaultTrack} />
          ))}
          {labels.fallback}
        </video>
      </div>
      {video.chapters?.length ? (
        <nav aria-label={labels.chapters} className="mt-3">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{labels.chapters}</p>
          <ol className="mt-2 flex flex-wrap gap-2">
            {video.chapters.map((chapter) => (
              <li key={chapter.t}>
                <button
                  type="button"
                  onClick={() => seek(chapter.t)}
                  aria-label={labels.jumpTo.replace('{time}', formatVideoTime(chapter.t)).replace('{title}', chapter.title)}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:border-blue-300 hover:text-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                >
                  <span className="font-mono text-xs text-blue-700">{formatVideoTime(chapter.t)}</span>
                  {chapter.title}
                </button>
              </li>
            ))}
          </ol>
        </nav>
      ) : null}
    </section>
  );
}
